import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  IconPhone,
  IconLock,
  IconUser,
  IconShield,
  IconInfoCircle,
  IconMail,
  IconRefresh,
  IconEye,
  IconEyeOff,
  IconMapPin,
} from "@tabler/icons-react";
import { notifications } from "@/lib/notifications";
import { useAuthStore } from "../../../store/authStore";
import GoogleButton from "../../../components/auth/GoogleButton";
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import styles from "./RegisterPage.module.scss";
import GeoCombobox from "../../../components/common/GeoCombobox";
import CountrySelect from "../../../components/common/CountrySelect";
import { getCountryCallingCode, isValidPhoneNumber, getCountries } from "libphonenumber-js";
import bdDistricts from "../../../data/bd-districts.json";
import SideImage from "../../../components/auth/SideImage";
import LanguageSwitcher from "../../../components/common/LanguageSwitcher";
import Logo from "../../../components/common/Logo";
import OtpBoxes from "../../../components/common/OtpBoxes";

const inputCls =
  "w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-zinc-700 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-400";

function PasswordField({ value, onChange, placeholder, error, leftIcon }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      {leftIcon && (
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
          {leftIcon}
        </span>
      )}
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${inputCls} ${leftIcon ? "pl-8" : ""} pr-10 ${error ? "border-red-500 focus:ring-red-400" : ""}`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
      >
        {show ? <IconEyeOff size={16} /> : <IconEye size={16} />}
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

// Valid Bangladeshi mobile number: 01 + operator digit (3-9) + 8 digits (11 total).
const BD_PHONE_REGEX = /^01[3-9]\d{8}$/;

// Common throwaway/disposable email providers — blocked so registrations use a real
// inbox (the backend enforces a fuller list; this gives instant feedback in the form).
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  "mailinator.com", "yopmail.com", "yopmail.fr", "guerrillamail.com", "guerrillamail.info",
  "sharklasers.com", "grr.la", "10minutemail.com", "temp-mail.org", "tempmail.com",
  "tempmailo.com", "throwawaymail.com", "throwaway.email", "trashmail.com", "getnada.com",
  "maildrop.cc", "mohmal.com", "dispostable.com", "fakeinbox.com", "mailnesia.com",
  "mailcatch.com", "spam4.me", "moakt.com", "emailondeck.com", "tempinbox.com",
]);
const isDisposableEmail = (email) => {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? DISPOSABLE_EMAIL_DOMAINS.has(domain) : false;
};

function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preRegister = useAuthStore((state) => state.preRegister);
  const sendOtp = useAuthStore((state) => state.sendOtp);
  const verifyOtp = useAuthStore((state) => state.verifyOtp);
  const completeRegistration = useAuthStore((state) => state.completeRegistration);
  const googleAuth = useAuthStore((state) => state.googleAuth);
  const verifyAndLogin = useAuthStore((state) => state.verifyAndLogin);
  const changeRegistrationPhone = useAuthStore((state) => state.changeRegistrationPhone);
  const sendRegistrationEmailOtp = useAuthStore((state) => state.sendRegistrationEmailOtp);
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const [loading, setLoading] = useState(false);
  // Auto-switch the UI to English when a non-Bangladesh country is chosen — but only
  // while the user is still on Bangla (i.e. hasn't picked a language themselves).
  const maybeSwitchToEnglish = (countryCode) => {
    if (countryCode && countryCode !== "BD" && currentLanguage !== "en") changeLanguage("en");
  };

  const fromLogin = location.state?.fromLogin === true;
  const [step, setStep] = useState(fromLogin ? 2 : 1);

  const isOperatorRegistration = location.pathname === "/register/operator";
  const defaultRole = isOperatorRegistration ? "OPERATOR" : "OWNER";

  const [formData, setFormData] = useState({
    country: "BD",
    phone: location.state?.phone || "",
    email: "",
    name: "",
    district: "",
    password: "",
    confirmPassword: "",
    otp: "",
    role: defaultRole,
    termsAccepted: false,
  });
  // Bangladesh verifies by SMS (email optional); every other country verifies by
  // email (email required) and uses an international phone number.
  const isBd = formData.country === "BD";
  // Dial code for the selected country (non-BD); used as the phone-field prefix.
  const dialCode = isBd ? "" : getCountryCallingCode(formData.country);
  // National digits shown in the non-BD phone field (formData.phone stores full E.164).
  const nationalPhone =
    !isBd && formData.phone.startsWith(`+${dialCode}`) ? formData.phone.slice(dialCode.length + 1) : "";

  // Tracks whether the user picked a country themselves — stops IP auto-detection
  // from overriding a manual choice.
  const countryTouchedRef = useRef(false);

  // The server accepts a code only ONCE — after `verifyOtp` it is spent. If the account-creation
  // call that follows fails, a retry must go straight to account creation: verifying the same code
  // again can only answer "invalid code" and would trap the user. Holds "<phone>|<purpose>".
  const verifiedOtpRef = useRef(null);
  // The 6th digit auto-submits AND Enter / the button submit the form — run only one at a time.
  const submittingRef = useRef(false);

  // Switch country: reset the phone (BD local vs E.164 formats differ) and clear
  // any stale phone/email errors so the fields re-validate for the new country.
  const handleCountryChange = (code) => {
    countryTouchedRef.current = true;
    setFormData((prev) => ({ ...prev, country: code, phone: "" }));
    setErrors((prev) => ({ ...prev, phone: undefined, email: undefined }));
    maybeSwitchToEnglish(code);
  };
  const [errors, setErrors] = useState({});

  // Best-effort IP-based country detection (no browser permission needed). Pre-selects
  // the visitor's country so non-BD users immediately get the email + free-text flow
  // (and the UI switches to English). Never overrides a manual choice; defaults to BD.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        if (!res.ok) return;
        const data = await res.json();
        const code = String(data?.country_code || data?.country || "").toUpperCase();
        if (cancelled || countryTouchedRef.current || code.length !== 2 || !getCountries().includes(code)) return;
        setFormData((prev) => (prev.country === code ? prev : { ...prev, country: code, phone: "" }));
        maybeSwitchToEnglish(code);
      } catch {
        /* ignore — country stays at the BD default */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [plan, setPlan] = useState("free");

  // "Try another way" state
  const [showAltOptions, setShowAltOptions] = useState(false);
  const [altMethod, setAltMethod] = useState(null); // 'phone' | 'email'
  const [altPhone, setAltPhone] = useState("");
  const [altEmail, setAltEmail] = useState("");
  const [altErrors, setAltErrors] = useState({});
  const [verificationMethod, setVerificationMethod] = useState("phone"); // 'phone' | 'email'

  const validateStep1 = () => {
    const newErrors = {};
    const trimmedPhone = formData.phone.trim();
    // Phone: BD uses the local 01XXXXXXXXX pattern; other countries validate the
    // international number against that country's rules via libphonenumber-js.
    if (!trimmedPhone) {
      newErrors.phone = t("validation.phoneRequired");
    } else if (isBd) {
      if (!BD_PHONE_REGEX.test(trimmedPhone)) newErrors.phone = t("validation.invalidPhone");
    } else if (!isValidPhoneNumber(trimmedPhone, formData.country)) {
      newErrors.phone = t("validation.invalidPhone");
    }
    // Email: required for non-BD (it's the OTP channel); optional but format-checked for BD.
    const trimmedEmail = formData.email.trim();
    const emailFormatOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (!isBd) {
      if (!trimmedEmail) newErrors.email = t("validation.emailRequired");
      else if (!emailFormatOk) newErrors.email = t("auth.invalidEmail");
      else if (isDisposableEmail(trimmedEmail)) newErrors.email = t("validation.disposableEmail");
    } else if (trimmedEmail) {
      if (!emailFormatOk) newErrors.email = t("auth.invalidEmail");
      else if (isDisposableEmail(trimmedEmail)) newErrors.email = t("validation.disposableEmail");
    }
    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = t("profile.nameMinLength");
    }
    if (!formData.district || !formData.district.trim()) {
      newErrors.district = t("validation.districtRequired");
    }
    const trimmedPassword = formData.password.trim();
    const trimmedConfirmPassword = formData.confirmPassword.trim();
    if (!trimmedPassword) {
      newErrors.password = t("validation.passwordRequired");
    } else if (trimmedPassword.length < 6) {
      newErrors.password = t("validation.passwordTooShort");
    }
    if (!trimmedConfirmPassword) {
      newErrors.confirmPassword = t("validation.confirmPasswordRequired");
    } else if (trimmedPassword !== trimmedConfirmPassword) {
      newErrors.confirmPassword = t("validation.passwordMismatch");
    }
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = t("validation.termsRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setShowAltOptions(false);
      setAltMethod(null);
      setVerificationMethod("phone");
    }
  };

  const handleSendOtp = async () => {
    if (!validateStep1()) return;
    setLoading(true);
    try {
      const emailValue = formData.email.trim() || null;
      await preRegister({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        district: formData.district.trim(),
        password: formData.password.trim(),
        role: formData.role,
        plan,
        country: formData.country,
        email: emailValue,
      });
      // BD → SMS OTP; other countries → email OTP. Track the channel so the OTP
      // screen, resend, and completion all use the matching purpose.
      const method = isBd ? "phone" : "email";
      setVerificationMethod(method);
      notifications.show({
        title: t("auth.otpSent"),
        message: method === "email" ? t("auth.checkEmailForOtp") : t("auth.checkPhoneForOtp"),
        color: "green",
      });
      setStep(2);
      startOtpTimer();
    } catch (error) {
      console.error("Pre-register error:", error);
      if (error.code === "USER_ALREADY_EXISTS") {
        notifications.show({ title: t("auth.accountExists"), message: t("auth.phoneAlreadyRegistered"), color: "blue" });
        return;
      }
      if (error.code === "REGISTRATION_ALREADY_STARTED") {
        notifications.show({
          title: t("common.warning"),
          message: t("errors.REGISTRATION_ALREADY_STARTED"),
          color: "yellow",
        });
        setStep(2);
        // No code was sent this time and the earlier one may already be used or expired — so don't
        // lock "resend" behind a 5-minute countdown; let them ask for a fresh code right away.
        setOtpTimer(0);
        setCanResendOtp(true);
        return;
      }
      // Backend rejects disposable/temporary emails — surface it on the email field.
      if (/disposable|temporary email/i.test(error?.message || "")) {
        setErrors((prev) => ({ ...prev, email: t("validation.disposableEmail") }));
        notifications.show({ title: t("common.error"), message: t("validation.disposableEmail"), color: "red" });
        return;
      }
      // Email already registered to another account.
      if (error?.code === "EMAIL_ALREADY_EXISTS" || /email is already in use/i.test(error?.message || "")) {
        setErrors((prev) => ({ ...prev, email: t("validation.emailAlreadyExists") }));
        notifications.show({ title: t("auth.accountExists"), message: t("validation.emailAlreadyExists"), color: "blue" });
        return;
      }
      // Blocked device / resend cooldown — the api interceptor already translated
      // these codes into error.message; show the real reason, not the generic failure.
      if (error?.code === "ACCESS_RESTRICTED" || error?.code === "OTP_RESEND_COOLDOWN") {
        notifications.show({ title: t("common.error"), message: error.message, color: "red" });
        return;
      }
      notifications.show({ title: t("common.error"), message: t("auth.failedToSendOtp"), color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    setLoading(true);
    try {
      if (verificationMethod === "email") {
        await sendRegistrationEmailOtp(formData.phone.trim(), formData.email.trim());
      } else {
        await sendOtp(formData.phone.trim());
      }
      verifiedOtpRef.current = null; // a fresh code has to be verified like any other
      notifications.show({ title: t("auth.otpResent"), message: t("auth.newOtpSent"), color: "green" });
      startOtpTimer();
    } catch (error) {
      console.error("Resend OTP error:", error);
      // Blocked device / resend cooldown — show the translated real reason.
      if (error?.code === "ACCESS_RESTRICTED" || error?.code === "OTP_RESEND_COOLDOWN") {
        notifications.show({ title: t("common.error"), message: error.message, color: "red" });
        return;
      }
      notifications.show({ title: t("common.error"), message: t("auth.failedToResendOtp"), color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const startOtpTimer = () => {
    setOtpTimer(300);
    setCanResendOtp(false);
    // Alt options (different phone number) stay locked until the new timer expires.
    setShowAltOptions(false);
    setAltMethod(null);
  };

  const handleSelectAltMethod = (method) => {
    setAltMethod(method);
    setAltErrors({});
    setAltPhone("");
    setAltEmail("");
  };

  const handleAltPhoneSubmit = async () => {
    const trimmed = altPhone.trim();
    if (!trimmed || !BD_PHONE_REGEX.test(trimmed)) {
      setAltErrors({ altPhone: t("validation.invalidPhone") });
      return;
    }
    setLoading(true);
    try {
      await changeRegistrationPhone(formData.phone.trim(), trimmed);
      setFormData((prev) => ({ ...prev, phone: trimmed, otp: "" }));
      setVerificationMethod("phone");
      setShowAltOptions(false);
      setAltMethod(null);
      notifications.show({ title: t("auth.otpSent"), message: t("auth.checkPhoneForOtp"), color: "green" });
      startOtpTimer();
    } catch (error) {
      console.error("Change phone error:", error);
      notifications.show({ title: t("common.error"), message: t("auth.changePhoneFailed"), color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleAltEmailSubmit = async () => {
    const trimmed = altEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setAltErrors({ altEmail: t("auth.invalidEmail") });
      return;
    }
    setLoading(true);
    try {
      await sendRegistrationEmailOtp(formData.phone.trim(), trimmed);
      setVerificationMethod("email");
      setShowAltOptions(false);
      setAltMethod(null);
      notifications.show({ title: t("auth.emailOtpSent"), message: t("auth.checkEmailForOtp"), color: "green" });
      startOtpTimer();
    } catch (error) {
      console.error("Send email OTP error:", error);
      notifications.show({ title: t("common.error"), message: t("auth.emailOtpFailed"), color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.otp || formData.otp.length !== 6 || !/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = t("auth.validOtpRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Verify the code, then create the account. Owns its loading state and its error toast, because
  // WHICH call failed decides what the user should read: only a rejected code may say "invalid code" —
  // a network, blocked-device or server failure while creating the account must say so instead.
  const doCompleteRegistration = async (otpValue) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    let stage = "otp";
    try {
      const otpPurpose = verificationMethod === "email" ? "registration_email" : "registration";
      const phone = formData.phone.trim();
      let result;
      if (fromLogin) {
        result = await verifyAndLogin(phone, otpValue, otpPurpose);
      } else {
        const verifiedKey = `${phone}|${otpPurpose}`;
        if (verifiedOtpRef.current !== verifiedKey) {
          await verifyOtp(phone, otpValue, otpPurpose);
          verifiedOtpRef.current = verifiedKey;
        }
        stage = "account";
        result = await completeRegistration({
          name: formData.name.trim(),
          phone,
          district: formData.district.trim(),
          password: formData.password.trim(),
          role: formData.role,
          plan,
          verificationMethod,
          country: formData.country,
          email: formData.email.trim() || null,
          purpose: otpPurpose,
        });
      }
      notifications.show({
        title: t("common.success"),
        message: fromLogin ? t("auth.phoneVerified") : t("auth.accountCreated"),
        color: "green",
      });
      if (result.user.role === "admin") {
        navigate("/admin");
      } else if (fromLogin) {
        navigate("/");
      } else {
        navigate("/onboard");
      }
    } catch (error) {
      console.error(stage === "otp" ? "OTP verification error:" : "Account creation error:", error);
      const rejectedCode = stage === "otp" && (error?.status === 400 || error?.status === 401);
      // The server no longer treats this number as verified (code expired) — the next try needs a fresh code.
      if (stage === "account" && error?.status === 401) verifiedOtpRef.current = null;
      notifications.show({
        title: t("common.error"),
        message: rejectedCode ? t("auth.invalidOtpTryAgain") : error?.message || t("auth.registrationFailed"),
        color: "red",
      });
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!validateStep3()) return;
    await doCompleteRegistration(formData.otp);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const progress = (step / 2) * 100;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryPlan = params.get("plan");
    setPlan(queryPlan === "pro" ? "pro" : "free");
  }, [location.search]);

  useEffect(() => {
    if (fromLogin) startOtpTimer();
  }, []);

  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (otpTimer === 0 && step === 2) {
      setCanResendOtp(true);
    }
  }, [otpTimer, step]);

  const handleOtpComplete = async (value) => {
    handleChange("otp", value);
    if (value && /^\d{6}$/.test(value)) await doCompleteRegistration(value);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className={styles.container}>
      {/* Left Side - Branding */}
      <div className={styles.brandingSection}>
        <SideImage />
      </div>

      {/* Right Side - Form */}
      <div className={styles.formSection}>
        {/* language switcher */}
        <div className={styles.languageSwitcher}>
          <LanguageSwitcher />
        </div>

        <div className={styles.formContainer}>
          {/* logo */}
          <div className={styles.logo}>
            <Link to="/" className="text-orange-500">
              <Logo w={150} m="auto" />
            </Link>
          </div>

          <p className="text-center text-sm text-zinc-500 dark:text-zinc-400 mb-4">
            {isOperatorRegistration ? t("auth.createOperatorAccount") : t("auth.createAccount")}
          </p>

          {/* Progress bar */}
          <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2 mb-6">
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Operator alert */}
          {isOperatorRegistration && (
            <div className="flex gap-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
              <IconInfoCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  {t("auth.operatorRegistrationTitle")}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  {t("auth.operatorRegistrationInfo")}
                </p>
              </div>
            </div>
          )}

          {/* Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-5 sm:p-7">
            {/* ── Step 1: Registration Form ── */}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                {/* Outside Bangladesh: register with Google instead of the email OTP (the email comes verified; the phone is asked for on the next page) */}
                {!isBd && (
                  <>
                    <GoogleButton
                      onClick={() => handleChange("termsAccepted", true)}
                      onToken={async (accessToken) => {
                        try {
                          const r = await googleAuth({ accessToken, country: formData.country });
                          navigate(r.user.role === "admin" ? "/admin" : r.isNew ? "/onboard" : "/");
                        } catch (err) {
                          notifications.show({ message: err?.message || t("auth.googleFailed"), color: "red" });
                        }
                      }}
                    />
                    <p className="-mt-2 text-center text-[11px] text-zinc-500 dark:text-zinc-400">
                      {t("auth.googleTermsNote")}{" "}
                      <a href="https://sohozkaj.com/terms" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600">{t("auth.termsLink")}</a>
                    </p>
                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                      <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                      {t("auth.orDivider")}
                      <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                    </div>
                  </>
                )}
                <div className="grid grid-cols-1 min-[1250px]:grid-cols-[35%_minmax(0,1fr)] gap-x-4 gap-y-2 items-start">
                {/* Country (35% of the row) + Phone (the rest) */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.country")} <span className="text-red-500">*</span>
                  </label>
                  <CountrySelect
                    value={formData.country}
                    onChange={handleCountryChange}
                    error={!!errors.country}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.phoneNumber")} <span className="text-red-500">*</span>
                  </label>
                  {isBd ? (
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                        <IconPhone size={16} />
                      </span>
                      <input
                        type="text"
                        placeholder={t("auth.phonePlaceholder")}
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        className={`${inputCls} pl-8 ${errors.phone ? "border-red-500 focus:ring-red-400" : ""}`}
                      />
                    </div>
                  ) : (
                    <div
                      className={`flex items-center rounded-md border ${
                        errors.phone ? "border-red-500 focus-within:ring-red-400" : "border-zinc-300 dark:border-zinc-600"
                      } bg-white dark:bg-zinc-700 focus-within:ring-2 focus-within:ring-orange-400`}
                    >
                      <span className="pl-3 pr-2 text-sm text-zinc-500 dark:text-zinc-300 select-none">+{dialCode}</span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        placeholder={t("auth.phonePlaceholder")}
                        value={nationalPhone}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "");
                          handleChange("phone", digits ? `+${dialCode}${digits}` : "");
                        }}
                        className="flex-1 px-2 py-2 bg-transparent text-sm rounded-r-md focus:outline-none dark:text-zinc-100 placeholder:text-zinc-400"
                      />
                    </div>
                  )}
                  {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                </div>
                </div>

                {/* Verification-channel hint, spanning the full row so it doesn't unbalance
                    the Country/Phone columns. */}
                <p className="-mt-2 text-[11px] text-zinc-400 flex items-center gap-1">
                  {isBd ? <IconPhone size={12} /> : <IconMail size={12} />}
                  {isBd ? t("auth.otpViaSms") : t("auth.otpViaEmail")}
                </p>

                <div className="flex flex-col gap-4">
                {/* Email — required for non-BD (OTP channel), optional for BD */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("profile.email")}{" "}
                    {isBd ? (
                      <span className="text-zinc-400 text-xs">({t("common.optional")})</span>
                    ) : (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                      <IconMail size={16} />
                    </span>
                    <input
                      type="email"
                      placeholder={t("auth.registerEmailPlaceholder")}
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={`${inputCls} pl-8 ${errors.email ? "border-red-500 focus:ring-red-400" : ""}`}
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>

                {/* Name (the rest of the row) + District (35%) */}
                <div className="grid grid-cols-1 min-[1250px]:grid-cols-[minmax(0,1fr)_35%] gap-4 items-start">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.registerNameLabel")} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                      <IconUser size={16} />
                    </span>
                    <input
                      type="text"
                      placeholder={t("auth.registerNamePlaceholder")}
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className={`${inputCls} pl-8 ${errors.name ? "border-red-500 focus:ring-red-400" : ""}`}
                    />
                  </div>
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>

                {/* District — BD picks from the district list; other countries type it free-form */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {isBd ? t("profile.district") : t("auth.registerStateLabel")} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
                      <IconMapPin size={16} />
                    </span>
                    {isBd ? (
                      <GeoCombobox
                        value={formData.district}
                        onChange={(v) => handleChange("district", v)}
                        options={bdDistricts.map((d) => ({ value: d.id, label: d.bn_name, en: d.name }))}
                        placeholder={t("profile.districtPlaceholder")}
                        className={`pl-8 ${errors.district ? "border-red-500 focus:ring-red-400" : ""}`}
                        selectOnly
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder={t("profile.districtPlaceholder")}
                        value={formData.district}
                        onChange={(e) => handleChange("district", e.target.value)}
                        className={`${inputCls} pl-8 ${errors.district ? "border-red-500 focus:ring-red-400" : ""}`}
                      />
                    )}
                  </div>
                  {errors.district && <p className="mt-1 text-xs text-red-500">{errors.district}</p>}
                </div>

                </div>
                </div>

                <div className="grid grid-cols-1 min-[1250px]:grid-cols-2 gap-4 items-start">
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.password")} <span className="text-red-500">*</span>
                  </label>
                  <PasswordField
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    placeholder={t("auth.registerPasswordPlaceholder")}
                    error={!!errors.password}
                    leftIcon={<IconLock size={16} />}
                  />
                  {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.confirmPassword")} <span className="text-red-500">*</span>
                  </label>
                  <PasswordField
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    placeholder={t("auth.registerConfirmPlaceholder")}
                    error={!!errors.confirmPassword}
                    leftIcon={<IconLock size={16} />}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>

                </div>

                {/* Terms */}
                <div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formData.termsAccepted}
                      onChange={(e) => handleChange("termsAccepted", e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-zinc-300 accent-orange-500"
                    />
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">
                      {t("auth.agreeTo")}
                      <a
                        href="https://sohozkaj.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:text-orange-600 ml-1"
                      >
                        {t("auth.terms")}
                      </a>
                    </span>
                  </label>
                  {errors.termsAccepted && (
                    <p className="mt-1 text-xs text-red-500">{errors.termsAccepted}</p>
                  )}
                </div>

                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSendOtp}
                  className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading && <Spinner />}
                  {t("auth.register")}
                </button>
              </div>
            )}

            {/* ── Step 2: OTP Verification ── */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp}>
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="text-center">
                    <IconShield
                      size={48}
                      className="mx-auto mb-3"
                      style={{ color: "#FF9900" }}
                    />
                    <h4 className="font-semibold text-base text-zinc-800 dark:text-zinc-100 mb-1">
                      {verificationMethod === "email"
                        ? t("auth.verifyEmail")
                        : t("auth.verifyPhoneNumber")}
                    </h4>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      {verificationMethod === "email"
                        ? t("auth.otpSentToEmail", { email: formData.email || altEmail })
                        : t("auth.otpSentTo", { phone: formData.phone })}
                    </p>
                  </div>

                  {/* OTP boxes */}
                  <div className="text-center">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                      {t("auth.enterVerificationCode")}
                    </p>
                    <OtpBoxes
                      value={formData.otp}
                      onChange={(val) => handleChange("otp", val)}
                      onComplete={handleOtpComplete}
                      autoFocus
                      error={!!errors.otp}
                    />
                    {errors.otp && (
                      <p className="mt-2 text-xs text-red-500">{errors.otp}</p>
                    )}
                  </div>

                  {/* Timer — alt options (different phone number) only unlock after the code expires */}
                  {otpTimer > 0 && (
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t("auth.codeExpiresIn", { time: formatTime(otpTimer) })}
                      </span>
                    </div>
                  )}

                  {canResendOtp && (
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t("auth.didntReceiveCode")}
                      </span>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={handleResendOtp}
                        className="text-sm text-orange-500 hover:text-orange-600 font-medium disabled:opacity-60"
                      >
                        {loading ? <Spinner /> : t("auth.resendOtp")}
                      </button>
                      {/* "Try another way" changes the phone + resends SMS, so it only
                          applies to phone (Bangladesh) verification. Email users just resend. */}
                      {verificationMethod === "phone" && (
                        <>
                          <span className="text-zinc-400">·</span>
                          <button
                            type="button"
                            className="text-sm text-orange-500 hover:text-orange-600"
                            onClick={() => {
                              setShowAltOptions((v) => !v);
                              setAltMethod(null);
                              setAltErrors({});
                            }}
                          >
                            {showAltOptions ? "বাতিল করুন" : "আরেকটি উপায় চেষ্টা করুন"}
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Alt options (Collapse) */}
                  {showAltOptions && verificationMethod === "phone" && (
                    <div className="border border-zinc-200 dark:border-zinc-700 rounded-md p-3 flex flex-col gap-3">
                      {altMethod === null && (
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => handleSelectAltMethod("phone")}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-sm font-medium text-zinc-700 dark:text-zinc-200 transition-colors"
                          >
                            <IconRefresh size={16} />
                            ভিন্ন ফোন নম্বর ব্যবহার করুন
                          </button>
                          {/* Email verification disabled — users verify via phone number only.
                          <button
                            type="button"
                            onClick={() => handleSelectAltMethod("email")}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-sm font-medium text-zinc-700 dark:text-zinc-200 transition-colors"
                          >
                            <IconMail size={16} />
                            ইমেইলে কোড পাঠান
                          </button>
                          */}
                        </div>
                      )}

                      {altMethod === "phone" && (
                        <div className="flex flex-col gap-2">
                          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">নতুন ফোন নম্বর দিন</p>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                              <IconPhone size={16} />
                            </span>
                            <input
                              type="tel"
                              inputMode="numeric"
                              maxLength={11}
                              placeholder="01XXXXXXXXX"
                              value={altPhone}
                              onChange={(e) => { setAltPhone(e.target.value.replace(/\D/g, "").slice(0, 11)); setAltErrors({}); }}
                              className={`${inputCls} pl-8 ${altErrors.altPhone ? "border-red-500" : ""}`}
                            />
                          </div>
                          {altErrors.altPhone && <p className="text-xs text-red-500">{altErrors.altPhone}</p>}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setAltMethod(null)}
                              className="px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700"
                            >
                              পিছনে
                            </button>
                            <button
                              type="button"
                              disabled={loading}
                              onClick={handleAltPhoneSubmit}
                              className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2"
                            >
                              {loading && <Spinner />} কোড পাঠান
                            </button>
                          </div>
                        </div>
                      )}

                      {altMethod === "email" && (
                        <div className="flex flex-col gap-2">
                          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">ইমেইল ঠিকানা দিন</p>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                              <IconMail size={16} />
                            </span>
                            <input
                              type="email"
                              placeholder="example@email.com"
                              value={altEmail}
                              onChange={(e) => { setAltEmail(e.target.value); setAltErrors({}); }}
                              className={`${inputCls} pl-8 ${altErrors.altEmail ? "border-red-500" : ""}`}
                            />
                          </div>
                          {altErrors.altEmail && <p className="text-xs text-red-500">{altErrors.altEmail}</p>}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setAltMethod(null)}
                              className="px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700"
                            >
                              পিছনে
                            </button>
                            <button
                              type="button"
                              disabled={loading}
                              onClick={handleAltEmailSubmit}
                              className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2"
                            >
                              {loading && <Spinner />} ইমেইলে কোড পাঠান
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Back / Verify buttons */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                    >
                      {t("common.back")}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {loading && <Spinner />}
                      {t("auth.verifyAndCreateAccount")}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          <p className="text-center mt-4 text-sm text-zinc-600 dark:text-zinc-400">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-orange-500 hover:text-orange-600 font-bold">
              {t("auth.signIn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
